// Clicks & orders › Partner rules (Phase 4b plan §2.6). Next to the clicks, so only what each rule
// did on this retailer's OWN pages: how often partners (own rule) or the rule's owner (another
// retailer's rule) were hidden here, split into shoppers near the store / in the province and shoppers
// we could not locate. Active balance limits keep their sentence (no counts). Managing the rules is
// Settings › Partner rules; the sentences there say what a rule does on both sides.
//
// Retailer comms style: simple English, short sentences, "HandOff", rounded numbers.
import React from 'react';
import { Box, Card, CardContent, Chip, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { formatDate, hiddenLabel, nearLabel, sectionTitle, windowChip } from '../settings/partnerRules/copy';

// Exact counts: real numbers for a short window, so rounding would hide nothing and add a word.
const count = (n) => (Number(n) || 0).toLocaleString('en-GB');

// A Balance Control limit (spec balance-control.md §3.6): that a limit exists and whose it is —
// never its width, counts or lift date.
function balanceSentence(limit) {
  if (limit.role === 'owner') {
    return limit.side === 'outbound'
      ? 'Your shop does not show partner products for now. This is a limit you set. It lifts by itself.'
      : 'Your shop is not shown on partner pages for now. This is a limit you set. It lifts by itself.';
  }
  const owner = limit.owner?.name;
  return limit.side === 'outbound'
    ? `${owner} does not show partner products for now. This is a limit they set. It lifts by itself.`
    : `${owner} is not shown on your pages for now. This is a limit they set. It lifts by itself.`;
}

// The total and its split side by side, one size: the split adds up to the total.
const Count = ({ label, value }) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{count(value)}</Typography>
  </Box>
);

// `ownWindow`: the section chip says "Last 7 days" but this rule started later, so the card says since when.
const RuleEffect = ({ card, ownWindow }) => (
  <Card variant="outlined" sx={{ mb: 1.5 }}>
    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography sx={{ fontWeight: 600 }}>{sectionTitle(card)}</Typography>
        {ownWindow && !card.started_today && (
          <Typography variant="caption" color="text.secondary">
            Since {formatDate(card.window_start)}
          </Typography>
        )}
      </Box>
      {card.started_today ? (
        <Typography color="text.secondary">
          No numbers yet. The rule started today. Numbers show here from tomorrow.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4, rowGap: 1.5 }}>
          <Count label={hiddenLabel(card)} value={card.hidden} />
          {card.near != null && (
            <>
              <Count label={nearLabel(card)} value={card.near} />
              <Count label="We could not locate the shopper" value={card.unknown} />
            </>
          )}
        </Box>
      )}
    </CardContent>
  </Card>
);

const BalanceLimit = ({ limit }) => (
  <Card variant="outlined" sx={{ mb: 1.5 }}>
    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
        <Chip size="small" variant="outlined" label="Balance limit" sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          Live since {formatDate(limit.since)}
        </Typography>
      </Box>
      <Typography variant="body2">{balanceSentence(limit)}</Typography>
    </CardContent>
  </Card>
);

const MatchExceptionsSection = ({ data }) => {
  if (!data || !data.rules) return null;
  const { rules, balance_limits: limits = [] } = data;
  const chip = windowChip(rules, data.window_days);
  return (
    <Box id="partner-rules" sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Partner rules
        </Typography>
        <Chip size="small" variant="outlined" label={chip} sx={{ color: 'text.secondary' }} />
      </Box>
      {rules.length === 0 && limits.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No rules. You appear on all partners&apos; pages, and all partners appear on yours.
        </Typography>
      ) : (
        <>
          {rules.map((card) => (
            <RuleEffect key={card.id} card={card} ownWindow={chip.startsWith('Last') && card.since_start} />
          ))}
          {limits.map((limit) => (
            <BalanceLimit key={limit.id} limit={limit} />
          ))}
        </>
      )}
      <Typography variant="body2" color="text.secondary">
        <Link component={RouterLink} to="/settings/partner-rules">
          Manage your partner rules in Settings.
        </Link>
      </Typography>
    </Box>
  );
};

export default MatchExceptionsSection;
